import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import HeroSlider from '../components/HeroSlider';
import Stats from '../components/Stats';
import WhyChooseUs from '../components/WhyChooseUs';
import FeaturedChef from '../components/FeaturedChef';
import PartnersMarquee from '../components/PartnersMarquee';
import ServiceModal from '../components/ServiceModal';
import { FaQuoteLeft } from 'react-icons/fa';
import Updates from '../components/Updates';
import './Home.css';

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

function Home() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  const [servicesRef, servicesIn] = useInView();
  const [testiRef, testiIn] = useInView();
  const [ctaRef, ctaIn] = useInView();

  useEffect(() => {
    axios.get('https://api.hospifylink.com/api/services')
      .then(r => { if (Array.isArray(r.data)) setServices(r.data); })
      .catch(() => {});

    axios.get('https://api.hospifylink.com/api/testimonials')
      .then(r => { if (Array.isArray(r.data)) setTestimonials(r.data); })
      .catch(() => {});
  }, []);

  const safeServices = Array.isArray(services) ? services : [];
  const safeTestimonials = Array.isArray(testimonials) ? testimonials : [];

  return (
    <div className="home-root">
      <HeroSlider />
      <Stats />
      <WhyChooseUs />
      <FeaturedChef />

      <section ref={servicesRef} className={`home-services ${servicesIn ? 'is-visible' : ''}`}>
        <h2 className="lca-section-title fc-anim fc-title">Our Services</h2>
        <p className="lca-section-sub fc-anim fc-subtitle">
          Hospitality staffing tailored to every need
        </p>

        <div className="home-services-grid">
          {safeServices.map((s, i) => (
            <div
              key={s.id}
              className="home-service-card fc-anim fc-card"
              style={{ animationDelay: servicesIn ? `${0.25 + i * 0.1}s` : '0s' }}
              onClick={() => setSelectedService(s)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setSelectedService(s); }}
            >
              <img src={s.image} alt={s.title} />
              <div className="home-service-overlay">
                <h3 className="home-service-title">{s.title}</h3>
                <p className="home-service-desc">{s.description}</p>
                <span className="home-service-cta">Learn more →</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section ref={testiRef} className={`home-testimonials ${testiIn ? 'is-visible' : ''}`}>
        <h2 className="lca-section-title fc-anim fc-title">What Our Clients Say</h2>
        <p className="lca-section-sub fc-anim fc-subtitle">
          Real experiences from real clients
        </p>

        <div className="home-testi-grid">
          {(safeTestimonials.length > 0
            ? safeTestimonials
            : [
                { name: 'Grace M.', text: 'The chef made our wedding unforgettable! Professional, punctual and incredibly talented.', role: 'Wedding Client' },
                { name: 'Eric K.', text: 'Booked a chef for my birthday party. Everyone was blown away by the food.', role: 'Party Host' },
                { name: 'Diane U.', text: 'The housekeeper transformed our home. Fresh, clean, and organized every day.', role: 'Home Client' }
              ]
          ).map((t, i) => (
            <div
              key={t.id || i}
              className="home-testi-card fc-anim fc-card"
              style={{ animationDelay: testiIn ? `${0.25 + i * 0.12}s` : '0s' }}
            >
              <FaQuoteLeft className="home-testi-quote" />
              <p>{t.text}</p>
              <h4>{t.name}</h4>
              <span>{t.role}</span>
            </div>
          ))}
        </div>
      </section>
      <Updates />
      <PartnersMarquee />

      <section ref={ctaRef} className={`home-cta ${ctaIn ? 'is-visible' : ''}`}>
        <div className="home-cta-inner fc-anim fc-card">
          <h2>Ready to Hire a Professional?</h2>
          <p>Make a request today and let us bring hospitality excellence to your event.</p>
          <a href="/professionals" className="lca-btn-primary home-cta-btn">
            Get Started
          </a>
        </div>
      </section>

      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </div>
  );
}

export default Home;
